import React from 'react';
import { twMerge } from 'tailwind-merge';

// ─── Types ────────────────────────────────────────────────────────────────────

type AlertType = 'error' | 'success' | 'info' | 'warning';
type AlertSize = 'sm' | 'md' | 'lg';

export type WebUiAlertProps = Omit<
  React.HTMLAttributes<HTMLDivElement>,
  'children'
> & {
  type: AlertType;
  size?: AlertSize;
  message: string;
  /** After this many seconds the alert fades out. Omit to keep it visible until unmounted. */
  autoHideAfterSeconds?: number;
  /** Fade duration in ms when auto-hiding (default 400). */
  fadeOutDurationMs?: number;
  /** Called after the fade-out finishes; use to clear message/error state in the parent. */
  onDismiss?: () => void;
  /** Show/hide the close button (default true). */
  closeable?: boolean;
};

// ─── Constants ────────────────────────────────────────────────────────────────

const COLOR: Record<AlertType, string> = {
  error: 'text-white bg-red-600',
  success: 'text-white bg-green-600',
  info: 'text-white bg-blue-600',
  warning: 'text-black bg-yellow-400',
};

const SIZE: Record<AlertSize, { wrap: string; icon: number; close: number }> = {
  sm: { wrap: 'text-xs px-2 py-1   gap-1.5', icon: 14, close: 14 },
  md: { wrap: 'text-sm px-3 py-2   gap-2', icon: 16, close: 16 },
  lg: { wrap: 'text-base px-4 py-3 gap-2.5', icon: 18, close: 18 },
};

// ─── Icons ────────────────────────────────────────────────────────────────────

function TypeIcon({ type, size }: { type: AlertType; size: number }) {
  const props = {
    width: size,
    height: size,
    viewBox: '0 0 24 24',
    fill: 'none',
    stroke: 'currentColor',
    strokeWidth: 2,
    strokeLinecap: 'round' as const,
    strokeLinejoin: 'round' as const,
    'aria-hidden': true,
  };

  if (type === 'success')
    return (
      <svg {...props}>
        <path d="M20 6 9 17l-5-5" />
      </svg>
    );
  if (type === 'error')
    return (
      <svg {...props}>
        <circle cx="12" cy="12" r="10" />
        <path d="m15 9-6 6M9 9l6 6" />
      </svg>
    );
  if (type === 'warning')
    return (
      <svg {...props}>
        <path d="m21.73 18-8-14a2 2 0 0 0-3.48 0l-8 14A2 2 0 0 0 4 21h16a2 2 0 0 0 1.73-3" />
        <path d="M12 9v4M12 17h.01" />
      </svg>
    );
  return (
    <svg {...props}>
      <circle cx="12" cy="12" r="10" />
      <path d="M12 16v-4M12 8h.01" />
    </svg>
  );
}

function CloseIcon({ size }: { size: number }) {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={2.5}
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden
    >
      <path d="M18 6 6 18M6 6l12 12" />
    </svg>
  );
}

// ─── Hook ─────────────────────────────────────────────────────────────────────

function useAutoHide(
  message: string,
  autoHideAfterSeconds: number | undefined,
  fadeOutDurationMs: number,
  onDismiss: (() => void) | undefined,
) {
  const autoHide =
    typeof autoHideAfterSeconds === 'number' && autoHideAfterSeconds > 0;
  const [visible, setVisible] = React.useState(true);
  const [fading, setFading] = React.useState(false);

  React.useEffect(() => {
    setVisible(true);
    setFading(false);
    if (!autoHide) return () => {};

    const id = window.setTimeout(
      () => setFading(true),
      autoHideAfterSeconds! * 1000,
    );
    return () => window.clearTimeout(id);
  }, [autoHide, autoHideAfterSeconds, message]);

  const dismiss = React.useCallback(() => {
    setFading(true);
    // For manual close without transition (e.g. closeable=true but no autoHide),
    // fall back to a timeout so the element still unmounts cleanly.
    if (!autoHide) {
      window.setTimeout(() => {
        setVisible(false);
        onDismiss?.();
      }, fadeOutDurationMs);
    }
  }, [autoHide, fadeOutDurationMs, onDismiss]);

  const handleTransitionEnd = React.useCallback(
    (e: React.TransitionEvent<HTMLDivElement>) => {
      if (e.propertyName !== 'opacity') return;
      if (parseFloat(getComputedStyle(e.currentTarget).opacity) < 0.05) {
        setVisible(false);
        onDismiss?.();
      }
    },
    [onDismiss],
  );

  return { visible, fading, dismiss, handleTransitionEnd };
}

// ─── Component ────────────────────────────────────────────────────────────────

export function WebUiAlert({
  type,
  size = 'md',
  message,
  className,
  autoHideAfterSeconds = 5,
  fadeOutDurationMs = 400,
  onDismiss,
  closeable = true,
  onTransitionEnd: userOnTransitionEnd,
  style: userStyle,
  ...rest
}: WebUiAlertProps) {
  const { visible, fading, dismiss, handleTransitionEnd } = useAutoHide(
    message,
    autoHideAfterSeconds,
    fadeOutDurationMs,
    onDismiss,
  );

  if (!visible) return null;

  const s = SIZE[size];

  return (
    <div
      role="alert"
      className={twMerge(
        'flex items-center rounded-md font-medium',
        s.wrap,
        COLOR[type],
        className,
      )}
      style={{
        ...userStyle,
        opacity: fading ? 0 : 1,
        transition: `opacity ${fadeOutDurationMs}ms ease-out`,
      }}
      {...rest}
      onTransitionEnd={(e) => {
        userOnTransitionEnd?.(e);
        handleTransitionEnd(e);
      }}
    >
      <TypeIcon type={type} size={s.icon} />

      <span className="flex-1">{message}</span>

      {closeable && (
        <button
          type="button"
          onClick={dismiss}
          aria-label="Close alert"
          className="-mr-0.5 ml-1 rounded opacity-70 transition-opacity hover:opacity-100 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-1 focus-visible:outline-current"
        >
          <CloseIcon size={s.close} />
        </button>
      )}
    </div>
  );
}
